// This is front end code, there is probaably a better location for this file
import type { Layout } from "react-grid-layout";
import type { CSSProperties } from "react";
import { ContentViews } from "pages/[orgId]/explore/lib/useContentView";
import { DeepLink } from "src/models";
import {
  ensureState,
  serializeParams,
  unserializeParams,
} from "src/search/lib/params-state-reducer";
import { SearchState } from "src/search/types";
import { Duration } from "src/utils/date";
import { TopicCardConfig } from "pages/[orgId]/topics/cards/types";

export enum DeepLinkType {
  BOARD = "dashboard-v1",
  EXPLORE = "explore-v1",
  // like explore-v1, but not saved by anyone, and not particularly intersting
  // -- instead, simple queries available as "sample" queries (e.g. when creating a new board)
  EXAMPLE = "example-v0",
  DIGEST = "digest-v1",
  DIGEST_2 = "digest-v2",
  SNIPPET = "snippet-v1",
  NARRATIVE = "narrative-v1",
  SPACE = "space-v1",
  COMPETENCY = "competency-v1",
}

// define competency stuff
export type CompetencyV1State = {
  topicId: string;
  queries: any[]; // TODO: define the structure of queries
};

export type SnippetV1State = {
  params: SearchState;
  isForCards: boolean;
  tags: string[];
};

// obsoleted
export type DigestV1State = {
  sections: DigestSection[];
};
// vestigial
export type DigestSection = {
  prompt: string;
  queries: SearchState[];
};

export interface DigestV2State extends DashboardV1State {
  status?: "draft" | "published";
  prompt: string;
  globalKeys?: string[];
  data: Record<string, BoardCardData>;
  links: Array<{ id: string; type: DeepLinkType }>;
  tags?: string[];
  name?: string;
  description?: string;
  topicCardConfig?: TopicCardConfig;
}

export type NarrativeV1State = {
  filterable?: string[]; // tbd representation of what filters to make available for generation
  defaultDateRange?: Partial<{
    duration: Duration;
    startDate: string;
    endDate: string;
  }>;
  defaultFilters?: SearchState["filters"]; // default values, retricted to the above
  digestIds: string[]; //
  suggestedDigestIds?: string[];
  tags?: string[];
  systemName?: string;
};

export type DashboardV1State = {
  style: CSSProperties;
  spacing: number;
  colorTheme?: "standard" | "alternate" | string;
  columnCount?: number;
  rowHeight?: number;
  layout: Layout[];
  defaultDateRange?: Partial<{
    duration: Duration;
    startDate: string;
    endDate: string;
  }>;
  defaultFilters?: SearchState["filters"];
  tags?: string[];
  systemName?: string;
  data: Record<string, BoardCardData>;
};

export type ExploreCard = BaseCardData & {
  type: DeepLinkType.EXPLORE;
  title: string;
  showTitle: boolean;
  centerTitle: boolean;
  snippetTags?: string[];
  mappings?: { type: "condition"; fromFieldKey: string; toFieldKey: string }[];
  state: {
    params: SearchState;
    viz: {
      value: ContentViews;
      config: any;
    };
  };
};

export type MarkdownCard = BaseCardData & {
  type: "markdown";
  markdown: string;
};

export type BaseCardData = {
  type: string;
  style: CSSProperties;
  transparent: boolean;
};

export type BoardCardData = BaseCardData & (ExploreCard | MarkdownCard);

export function isExploreCard(
  card: ExploreCard | MarkdownCard
): card is ExploreCard {
  return card.type === DeepLinkType.EXPLORE;
}

export function isMarkdownCard(
  card: ExploreCard | MarkdownCard
): card is MarkdownCard {
  return card.type === "markdown";
}

export type ExploreV1State = {
  serialized: string;
  viz: { name: string; value: string; config: any }[];
  digestDisplayHint?: { version: string; header: string };
};

export function toExploreV1State(
  state: string,
  orgId?: string
): ExploreV1State {
  try {
    const data = JSON.parse(state);
    const serialized = serializeParams(
      ensureState(unserializeParams(data.serialized), orgId)
    );
    return {
      serialized,
      viz: Array.isArray(data.viz) ? data.viz : [],
      digestDisplayHint: data.digestDisplayHint,
    };
  } catch {
    return { serialized: "", viz: [] };
  }
}

export function fromExploreV1State(data: ExploreV1State): string {
  return JSON.stringify({
    serialized: data.serialized,
    viz: data.viz,
    digestDisplayHint: data.digestDisplayHint,
  });
}

export type Availability = {
  populated: boolean;
  configured: boolean;
};

export type AnnotatedDeepLink<T = unknown> = DeepLink & {
  hitCount?: number;
  lastHit?: string;
  bookmarkCount?: number;
  isBookmarked?: boolean;
  availability?: Availability;
} & { json: T };

// Footer options for cards (duplicated here for DigestV2State)
export interface FooterOptions {
  showTags?: boolean;
  showProcessedRecords?: boolean;
  enableShare?: boolean;
}
